import React, { useEffect, useState, useRef } from 'react';
import { Star, Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { CTAButton } from './CTAButton';
const AUDIO_URL = 'https://iniciadasritual.site/wp-content/uploads/2026/02/Voice-of-freedom.mp3';
export function HeroSection() {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(78);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => {
      if (audio.duration && isFinite(audio.duration)) {
        setDuration(audio.duration);
      }
    };
    const onEnded = () => setIsPlaying(false);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('loadedmetadata', onLoadedMetadata);
    audio.addEventListener('ended', onEnded);
    return () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('loadedmetadata', onLoadedMetadata);
      audio.removeEventListener('ended', onEnded);
    };
  }, []);
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) audio.pause();else audio.play();
    setIsPlaying(!isPlaying);
  };
  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    audio.currentTime = x / rect.width * duration;
  };
  const handleVolumeClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const newVol = Math.max(0, Math.min(1, x / rect.width));
    setVolume(newVol);
    setIsMuted(false);
    if (audioRef.current) {
      audioRef.current.volume = newVol;
      audioRef.current.muted = false;
    }
  };
  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };
  const formatTime = (s: number) => {
    const mins = Math.floor(s / 60);
    const secs = Math.floor(s % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const progress = duration > 0 ? currentTime / duration * 100 : 0;
  return <section className="bg-navy text-white pt-8 pb-12 px-4 relative overflow-hidden" data-id="element-199">
      <audio ref={audioRef} src={AUDIO_URL} preload="metadata" data-id="element-200" />

      <div className="absolute inset-0 opacity-5 bg-[url('https://upload.wikimedia.org/wikipedia/en/a/a4/Flag_of_the_United_States.svg')] bg-cover bg-center pointer-events-none" data-id="element-201" />

      <div className="max-w-3xl mx-auto flex flex-col items-center text-center relative z-10" data-id="element-202">
        <div className="flex items-center gap-3 mb-6" data-id="element-203">
          <span className="bg-patriot-red text-white text-xs font-bold px-3 py-1 rounded-sm tracking-wider uppercase" data-id="element-204">
            Bestseller
          </span>
          <div className="flex text-gold" data-id="element-205">
            {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" className="text-gold" data-id="element-206" />)}
          </div>
          <span className="text-blue-300 text-xs font-medium" data-id="element-207">
            12,000+ Patriots
          </span>
        </div>

        <h1 className="text-3xl md:text-5xl font-bold mb-4 leading-tight" data-id="element-208">
          Charlie Kirk: Unfiltered
        </h1>

        <h2 className="text-lg md:text-2xl text-patriot-red mb-4 font-bold italic max-w-2xl" data-id="element-209">
          The Story They Won&#39;t Tell You — In His Own Words.
        </h2>

        <p className="text-lg md:text-xl text-blue-200 mb-4 font-medium max-w-2xl" data-id="element-210">
          A collector-style digital bundle that captures Charlie&#39;s life,
          defining moments, and the legacy that sparked a movement.
        </p>

        <p className="text-base text-blue-300 mb-8 italic max-w-xl" data-id="element-211">
          Read it tonight. Listen on your drive tomorrow. Share it with your
          family this weekend.
        </p>

        <div className="relative w-full max-w-md mx-auto mt-2 mb-8" data-id="element-212">
          <div className="absolute -top-4 -right-4 bg-patriot-red text-white text-xs font-bold px-4 py-4 rounded-full w-20 h-20 flex items-center justify-center text-center shadow-lg z-10 border-2 border-navy rotate-12" data-id="element-213">
            NOW
            <br data-id="element-214" />
            AVAILABLE
            <br data-id="element-215" />
            ONLINE
          </div>
          <div className="relative rounded-xl overflow-hidden shadow-2xl border-4 border-navy-light bg-black" data-id="element-216">
            <img src="https://cdn.magicpatterns.com/uploads/8VhdCTKEB5SoAzmACwizSF/Mockup_Audio_Book_Charlie_Kirk.png" alt="Voices of Freedom Audiobook Bundle" className="w-full h-auto object-cover opacity-90 hover:opacity-100 transition-opacity" data-id="element-217" />
          </div>
        </div>

        <div className="w-full max-w-md mx-auto" data-id="element-218">
          <p className="text-blue-300 text-sm font-bold uppercase tracking-widest mb-4" data-id="element-219">
            🎧 Listen To A Free Preview
          </p>

          <div className="bg-white rounded-xl p-6 shadow-xl text-left" data-id="element-220">
            <h3 className="text-navy font-bold text-xl mb-2" data-id="element-221">
              Listen to the first chapter — free.
            </h3>
            <p className="text-gray-500 text-sm mb-6" data-id="element-222">
              Press play and hear a preview from <em data-id="element-223">Voices of Freedom</em>{' '}
              before you download the full bundle.
            </p>

            <div className="flex items-center gap-4 mb-6" data-id="element-224">
              <button onClick={togglePlay} className="w-14 h-14 rounded-full bg-navy flex items-center justify-center text-white hover:bg-navy-light transition-colors flex-shrink-0 shadow-md" aria-label={isPlaying ? 'Pause preview' : 'Play preview'} data-id="element-225">
                {isPlaying ? <Pause size={24} fill="currentColor" data-id="element-226" /> : <Play size={24} className="ml-1" fill="currentColor" data-id="element-227" />}
              </button>
              <div className="flex-1" data-id="element-228">
                <div className="h-2 bg-gray-200 rounded-full w-full mb-2 relative overflow-hidden cursor-pointer" onClick={handleProgressClick} data-id="element-229">
                  <div className="absolute top-0 left-0 h-full bg-navy rounded-full transition-[width] duration-150" style={{
                  width: `${progress}%`
                }} data-id="element-230" />
                </div>
                <div className="flex justify-between text-xs text-gray-500 font-mono" data-id="element-231">
                  <span data-id="element-232">{formatTime(currentTime)}</span>
                  <span data-id="element-233">{formatTime(duration)}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-6 text-gray-400" data-id="element-234">
              <button onClick={toggleMute} className="hover:text-navy transition-colors" aria-label={isMuted ? 'Unmute' : 'Mute'} data-id="element-235">
                {isMuted ? <VolumeX size={18} data-id="element-236" /> : <Volume2 size={18} data-id="element-237" />}
              </button>
              <div className="h-1.5 bg-gray-200 rounded-full w-24 cursor-pointer relative" onClick={handleVolumeClick} data-id="element-238">
                <div className="h-full bg-navy rounded-full" style={{
                width: `${isMuted ? 0 : volume * 100}%`
              }} data-id="element-239" />
              </div>
            </div>

            <CTAButton onClick={() => document.getElementById('offer')?.scrollIntoView({
            behavior: 'smooth'
          })} data-id="element-240">
              YES — SHOW ME THE BUNDLE DETAILS →
            </CTAButton>
          </div>
        </div>
      </div>
    </section>;
}